import React from 'react';
import Layout from '../../components/Layout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Download, TrendingUp, Award, Clock, BookOpen } from 'lucide-react';

const StudentStats: React.FC = () => {
  // Mock data - replace with actual API calls
  const progressData = [
    { month: 'Jan', courses: 1, quizzes: 2, hours: 8 },
    { month: 'Fév', courses: 2, quizzes: 5, hours: 15 },
    { month: 'Mar', courses: 3, quizzes: 8, hours: 24 },
    { month: 'Avr', courses: 3, quizzes: 12, hours: 32 },
    { month: 'Mai', courses: 4, quizzes: 15, hours: 38 },
    { month: 'Juin', courses: 5, quizzes: 18, hours: 45 },
  ];

  const quizScores = [
    { name: 'Quiz GPS 1', score: 85 },
    { name: 'Quiz GPS 2', score: 92 },
    { name: 'Quiz GPS 3', score: 78 },
    { name: 'Quiz Charge 1', score: 88 },
    { name: 'Quiz Charge 2', score: 95 },
    { name: 'Quiz Réath 1', score: 82 },
  ];

  const coursesProgress = [
    { name: 'GPS & Données', value: 85, color: '#2D8659' },
    { name: 'Charge d\'entraînement', value: 60, color: '#4FC08A' },
    { name: 'Réathlétisation', value: 25, color: '#0F482F' },
  ];

  const timeDistribution = [
    { name: 'Vidéos', value: 65, color: '#1A6542' },
    { name: 'Lectures', value: 25, color: '#8FDCB3' },
    { name: 'Quiz', value: 10, color: '#2D8659' },
  ];

  const globalStats = {
    totalStudyTime: 45,
    coursesCompleted: 1,
    coursesInProgress: 2,
    averageScore: 87,
    quizzesTaken: 6,
    totalModules: 24,
    completedModules: 16
  };

  const exportToPDF = () => {
    alert('Export PDF en cours de développement...');
  };

  const exportToCSV = () => {
    alert('Export CSV en cours de développement...');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Mes Statistiques</h1>
          <div className="flex space-x-3">
            <button
              onClick={exportToPDF}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export PDF
            </button>
            <button
              onClick={exportToCSV}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Global Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Clock className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Temps d'étude total</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.totalStudyTime}h</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <BookOpen className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Cours terminés</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.coursesCompleted}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <Award className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Score moyen</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.averageScore}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-brand-100 rounded-lg">
                <TrendingUp className="h-6 w-6 text-brand-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Modules terminés</p>
                <p className="text-2xl font-bold text-gray-900">{globalStats.completedModules}/{globalStats.totalModules}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Progress Over Time */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression mensuelle</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={progressData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="hours" stroke="#2D8659" strokeWidth={2} name="Heures d'étude" />
                <Line type="monotone" dataKey="quizzes" stroke="#1A6542" strokeWidth={2} name="Quiz réalisés" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Quiz Scores */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scores des quiz</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={quizScores}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="score" fill="#2D8659" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Course Progress */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression par cours</h3>
            <div className="space-y-4">
              {coursesProgress.map((course, index) => (
                <div key={index}>
                  <div className="flex justify-between text-sm font-medium text-gray-900 mb-1">
                    <span>{course.name}</span>
                    <span>{course.value}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${course.value}%`,
                        backgroundColor: course.color
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Time Distribution */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition du temps</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={timeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#0F482F"
                  dataKey="value"
                >
                  {timeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Stats Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Statistiques détaillées</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Métrique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valeur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Objectif
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progression
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Temps d'étude cette semaine
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">8h 30min</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">10h</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-brand-400 h-2 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                      <span className="text-sm text-gray-900">85%</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Quiz réussis ({'>'}80%)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">5/6</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">6/6</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-brand-500 h-2 rounded-full" style={{ width: '83%' }}></div>
                      </div>
                      <span className="text-sm text-gray-900">83%</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    Modules terminés ce mois
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">6</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">8</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                        <div className="bg-brand-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                      </div>
                      <span className="text-sm text-gray-900">75%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentStats;